import { Skeleton, Stack } from "@mui/material"
import { Dispatch, SetStateAction, useContext } from "react"
import { MainContext } from "../../controllers/main"
import {
  desktopMaxWidth,
  footerHeight,
  navBarHeight,
} from "../../services/config/constants"
import BreadcrumbDesktop from "../global/desktop/BreadcrumbDesktop"
import { colors } from "../../services/config/colors"
import ActionDetailsBackgroundDesktop from "../../assets/svgComponents/ActionDetailsBackgroundDesktop"
import AppearFromSide from "../animations/AppearFromSide"
import Title from "../global/common/Title"
import FadeFromTop from "../animations/FadeFromTop"
import Button from "../global/common/Button"
import PaddingContainerDesktop from "../global/desktop/PaddingContainerDesktop"
import { useTranslation } from "react-i18next"
import { MissionPeriod, MissionType } from "../../services/config/enum"
import PointsBadge from "../global/common/PointsBadge"
import learnMissionIconWhite from "../../assets/icons/missions/missionsTypes/learn-white.svg"
import Text from "../global/common/Text"
import MissionCompletedFeedback from "../feedbacks/common/MissionCompletedFeedback"
import { QuizzesContext } from "../../controllers/quizzes"
import ToggleButton from "../global/common/ToggleButton"

const QuizDesktop = ({
  answer,
  setAnswer,
  isAnswerCorrect,
  missionFeedbackOpen,
  onQuizSubmit,
}: {
  answer: string
  setAnswer: Dispatch<SetStateAction<string>>
  isAnswerCorrect: boolean | null
  missionFeedbackOpen: boolean
  onQuizSubmit: () => void
}) => {
  const { t } = useTranslation()
  const { windowHeight, currentMission } = useContext(MainContext)
  const { quizzesLoading, submitLoading, currentQuiz, submitQuiz } =
    useContext(QuizzesContext)

  return !currentMission ? null : (
    <Stack
      style={{
        width: "100%",
        minHeight:
          windowHeight > 1200 ? `calc(100% - ${footerHeight}px)` : 1200,
        position: "relative",
      }}
    >
      {/* navbar background */}
      <div
        style={{
          width: "100%",
          height: navBarHeight,
          backgroundColor: colors.background,
        }}
      />
      {/* breadcrumb */}
      <BreadcrumbDesktop
        items={[
          {
            label: t("learn_mission"),
          },
        ]}
        selectedItem={0}
        style={{ marginTop: 22, zIndex: 1 }}
      />
      {/* main container */}
      <PaddingContainerDesktop>
        <Stack
          style={{
            width: "100%",
            maxWidth: desktopMaxWidth,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* header */}
          <AppearFromSide fade style={{ marginTop: 22 }}>
            <Stack direction="row" gap={1}>
              <Stack
                direction="row"
                alignItems="center"
                style={{
                  width: "auto",
                  height: 40,
                  borderRadius: 4,
                  paddingInline: 10,
                  backgroundColor: colors.missions.learn,
                }}
              >
                <img src={learnMissionIconWhite} style={{ width: 23 }} alt="" />
                <Title
                  fontSize={18}
                  color={colors.textWhite}
                  style={{
                    fontVariant: "all-small-caps",
                    marginLeft: 8,
                    marginBottom: 2,
                  }}
                >
                  {t("learn")}
                </Title>
                <Title
                  fontSize={18}
                  color={colors.textWhite}
                  style={{
                    fontVariant: "all-small-caps",
                    marginLeft: 18,
                    marginBottom: 2,
                  }}
                >
                  {currentMission.current}/{currentMission.target}
                </Title>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                style={{
                  width: "auto",
                  height: 40,
                  borderRadius: 4,
                  paddingInline: 10,
                  border: "1.5px solid " + colors.tertiary,
                  backgroundColor: colors.backgroundWhite,
                }}
              >
                <Title fontSize={18} color={colors.tertiary}>
                  {currentMission.period === MissionPeriod.DAILY
                    ? t("today")
                    : currentMission.period === MissionPeriod.WEEKLY
                    ? t("week")
                    : t("special_singular")}
                </Title>
              </Stack>
              <PointsBadge
                points={currentMission.points}
                pointPerActivity={currentMission.pointPerActivity}
              />
            </Stack>
          </AppearFromSide>
          <AppearFromSide fade delay={0.05} style={{ marginTop: 16 }}>
            <Title fontSize={28}>{currentMission.title}</Title>
            {currentMission.description ? (
              <Text fontSize={18} fontWeight={400} style={{ marginTop: 8 }}>
                {currentMission.description}
              </Text>
            ) : null}
          </AppearFromSide>
          {/* quiz */}
          <FadeFromTop delay={0.05} style={{ marginTop: 50 }}>
            {quizzesLoading || !currentQuiz ? (
              <Stack gap={4} style={{ width: "100%" }}>
                <Stack>
                  <Skeleton width="100%" height={36} />
                  <Skeleton width="85%" height={36} />
                </Stack>
                <Stack gap={3}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    style={{ borderRadius: 24 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    style={{ borderRadius: 24 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    style={{ borderRadius: 24 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    style={{ borderRadius: 24 }}
                  />
                </Stack>
              </Stack>
            ) : (
              <Stack gap={4} style={{ width: "100%" }}>
                <Title fontSize={26}>{currentQuiz.document.question}</Title>
                <Stack gap={3}>
                  <ToggleButton
                    option={{ id: "opt1", label: currentQuiz.document.opt1 }}
                    onClick={(newValue) => setAnswer(newValue.id)}
                    selected={answer === "opt1"}
                    success={
                      isAnswerCorrect !== null &&
                      currentQuiz.correctAnswer === "opt1"
                    }
                    error={
                      answer === "opt1" && isAnswerCorrect === false
                        ? true
                        : false
                    }
                    disabled={isAnswerCorrect !== null}
                    withIcons
                  />
                  <ToggleButton
                    option={{ id: "opt2", label: currentQuiz.document.opt2 }}
                    onClick={(newValue) => setAnswer(newValue.id)}
                    selected={answer === "opt2"}
                    success={
                      isAnswerCorrect !== null &&
                      currentQuiz.correctAnswer === "opt2"
                    }
                    error={
                      answer === "opt2" && isAnswerCorrect === false
                        ? true
                        : false
                    }
                    disabled={isAnswerCorrect !== null}
                    withIcons
                  />
                  {currentQuiz.document.opt3 ? (
                    <ToggleButton
                      option={{ id: "opt3", label: currentQuiz.document.opt3 }}
                      onClick={(newValue) => setAnswer(newValue.id)}
                      selected={answer === "opt3"}
                      success={
                        isAnswerCorrect !== null &&
                        currentQuiz.correctAnswer === "opt3"
                      }
                      error={
                        answer === "opt3" && isAnswerCorrect === false
                          ? true
                          : false
                      }
                      disabled={isAnswerCorrect !== null}
                      withIcons
                    />
                  ) : null}
                  {currentQuiz.document.opt4 ? (
                    <ToggleButton
                      option={{ id: "opt4", label: currentQuiz.document.opt4 }}
                      onClick={(newValue) => setAnswer(newValue.id)}
                      selected={answer === "opt4"}
                      success={
                        isAnswerCorrect !== null &&
                        currentQuiz.correctAnswer === "opt4"
                      }
                      error={
                        answer === "opt4" && isAnswerCorrect === false
                          ? true
                          : false
                      }
                      disabled={isAnswerCorrect !== null}
                      withIcons
                    />
                  ) : null}
                </Stack>
              </Stack>
            )}
          </FadeFromTop>
          {/* log button */}
          <FadeFromTop
            delay={0.05}
            style={{
              marginTop: 68,
              marginBottom: 76,
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button
              loading={submitLoading}
              disabled={!answer}
              style={{ maxWidth: 510 }}
              onClick={onQuizSubmit}
              title={t("continue")}
            >
              {t("continue")}
            </Button>
          </FadeFromTop>
        </Stack>
      </PaddingContainerDesktop>
      {/* background */}
      <div
        style={{
          width: "100%",
          maxWidth: 1920,
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
          position: "absolute",
          top: 0,
          right: 0,
        }}
      >
        <ActionDetailsBackgroundDesktop
          color={colors.background}
          style={{ position: "absolute", top: 410 }}
        />
      </div>
      {/* feedback */}
      <MissionCompletedFeedback
        open={missionFeedbackOpen}
        missionType={MissionType.LEARN}
        isFailed={isAnswerCorrect === false}
      />
    </Stack>
  )
}

export default QuizDesktop
